import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import { ArrowRight, Layers, Radio } from 'lucide-react';
import type { ClientPlatformSummary, ClientServiceBasis } from '@shared/types';

const BASIS_LABELS: Record<ClientServiceBasis, string> = {
  package: 'Included in package',
  configured: 'Configured for this client',
  activity: 'Used in the last 12 months',
};

interface PlatformSummaryCardProps {
  clientID: string;
  summary: ClientPlatformSummary;
}

export function PlatformSummaryCard({ clientID, summary }: PlatformSummaryCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Layers className="h-4 w-4" />
          Platform
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Link
          to="/events/reports"
          search={{ clientID, startDate: summary.range.start, endDate: summary.range.end }}
          className="flex items-center justify-between rounded-md border p-3 hover:bg-accent transition-colors"
        >
          <div className="flex items-center gap-2 text-sm">
            <Radio className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{summary.liveEventCount.toLocaleString()}</span>
            <span className="text-muted-foreground">live events in the last 12 months</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </Link>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Services:</span>
          {summary.services.length === 0 ? (
            <span className="text-muted-foreground italic">None detected</span>
          ) : (
            summary.services.map((service) => (
              <Tooltip key={service.key}>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="text-xs cursor-help">
                    {service.label}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {service.basis.map((b) => (
                    <p key={b}>{BASIS_LABELS[b]}</p>
                  ))}
                </TooltipContent>
              </Tooltip>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
